import {useEffect} from 'react';

import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {reaction} from 'mobx';
import {Observer} from 'mobx-react';

import type {PaymentExpandedCommon} from '@yourcoach/shared/api/payment';

import {useI18n} from '@yourcoach/i18n';
import {cardStore} from '@yourcoach/shared/api/payment';
import {alert} from '@yourcoach/shared/components/alert';
import {
  MakeRefundContainer,
  MakeRefundStore,
} from '@yourcoach/shared/modules/payments';
import {isCancelCommand} from '@yourcoach/shared/services/entity-action/utils';
import {handle3dSecure} from '@yourcoach/shared/utils/entity/card';
import {logger} from '@yourcoach/shared/utils/logger';
import {useMobxStore} from '@yourcoach/shared/utils/store';

import {ModalStickyFooter} from '@src/components/ModalNew';

import {AddCardModal} from '../add-card-modal';
import * as S from './styles';

type Props = {
  payment: PaymentExpandedCommon;
};

export const MakeRefundModal = NiceModal.create<Props>(({payment}) => {
  const {t} = useI18n();
  const modal = useModal();

  const store = useMobxStore(() => new MakeRefundStore(payment));

  useEffect(() =>
    reaction(
      () => cardStore.creating,
      creating => {
        if (creating.success && creating.entity) {
          NiceModal.hide(AddCardModal);
        }
      },
    ),
  );

  const onRefundButtonClick = async () => {
    try {
      const result = await store.makeRefund({
        handle3dSecure,
        returnUrl: window.location.href,
      });

      if (isCancelCommand(result)) {
        if (!store.isFormValid) {
          store.fields.amount.ref.current?.focus();
        }

        return;
      }

      modal.hide();
    } catch (error) {
      logger.error(error);

      alert.error(error);
    }
  };

  const onAddCardButtonClick = () => {
    NiceModal.show(AddCardModal);
  };

  return (
    <Observer>
      {() => (
        <S.Modal
          bodyClassName="body"
          customScroll
          isOpen={modal.visible}
          onAfterClose={modal.remove}
          onRequestClose={modal.hide}
          showLoadingOverlay={store.busy.getReason('makeRefund')}
          title={t('shared:refund_payment')}
        >
          <MakeRefundContainer
            onAddCardButtonClick={onAddCardButtonClick}
            store={store}
          />
          <ModalStickyFooter position={20}>
            <S.FooterButton onClick={onRefundButtonClick}>
              <S.FooterButtonText>{t('shared:continue')}</S.FooterButtonText>
            </S.FooterButton>
          </ModalStickyFooter>
        </S.Modal>
      )}
    </Observer>
  );
});
